import invariant from 'invariant';
import graphql from 'api/graphql';
import { fetchGigyaJWTPromise } from 'api/gigya';

import { GIGYA_BRAND_IMAGE_FAVORITES } from 'globalConst/gigya-const';

import * as analytics from 'analytics';
import { navigateToLoginScreen } from 'actions/gigya-router-actions';
import { showNotification } from 'actions/notifications-actions';
import { NotificationTypes, NotificationTexts } from 'components/Notifications/Notification';

import { favoriteActions, favoritePodcastActions } from './actions-const';
import logger from 'util/logger';

/**
 * Add to favorite Podcast
 * @param  {object} podcast
 */
function addFavoritePodcast(item) {
  return async (dispatch, getState) => {
    const {
      favorites,
      user: { isLoggedIn },
    } = getState();
    const dispatchType = isLoggedIn
      ? favoritePodcastActions.FAVORITE_PODCAST
      : favoritePodcastActions.QUEUE_FAVORITE_PODCAST;

    dispatch({
      type: dispatchType,
      payload: {
        ...item,
        updatedAt: Date.now(),
      },
    });

    if (isLoggedIn) {
      try {
        const jwtToken = await fetchGigyaJWTPromise();
        await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, true);

        dispatch(
          showNotification({
            message: NotificationTexts.PODCAST_ADD_TO_COLLECTION,
          })
        );

        analytics.trackFavoritePodcast(item);
      } catch (e) {
        // rollback
        dispatch({
          type: favoriteActions.ROLLBACK_FAVORITES,
          payload: favorites,
        });

        dispatch(
          showNotification({
            message: NotificationTexts.PODCAST_ADD_TO_COLLECTION_ERROR,
            type: NotificationTypes.ERROR,
          })
        );
        logger.error(e);
      }
    }
  };
}

/**
 * Remove favorite Podcast
 * @param  {object} signature
 * @param  {object} podcast
 */
function removefavoritePodcast(item) {
  return async (dispatch, getState) => {
    const { favorites } = getState();

    // optimistic UI
    dispatch({
      type: favoritePodcastActions.UNFAVORITE_PODCAST,
      payload: item.id,
    });

    try {
      const jwtToken = await fetchGigyaJWTPromise();

      await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, false);

      dispatch(
        showNotification({
          message: NotificationTexts.PODCAST_REMOVE_FROM_COLLECTION,
        })
      );

      analytics.trackUnfavoritePodcast(item);
    } catch (e) {
      // rollback
      dispatch({
        type: favoriteActions.ROLLBACK_FAVORITES,
        payload: favorites,
      });

      dispatch(
        showNotification({
          message: NotificationTexts.PODCAST_REMOVE_FROM_COLLECTION_ERROR,
          type: NotificationTypes.ERROR,
        })
      );
      logger.error(e);
    }
  };
}

/**
 * Toggle Add/Remove Podcast
 * @param {object} item
 */
export function favoritePodcastToggle(item) {
  return async (dispatch, getState) => {
    const {
      favorites: { podcasts: currentFavoritePodcasts },
      user: { isLoggedIn, signature },
    } = getState();

    if (!isLoggedIn) {
      analytics.gigyaExpand('add-my-category');
      navigateToLoginScreen(GIGYA_BRAND_IMAGE_FAVORITES);
    } else {
      invariant(signature, 'No signature found.');
    }

    const isFavorite = currentFavoritePodcasts.some(
      (favorite) => (favorite.slug && favorite.slug === item.slug) || (favorite.id && favorite.id === item.id)
    );

    if (isFavorite) {
      dispatch(removefavoritePodcast(item));
    } else {
      dispatch(addFavoritePodcast(item));
    }
  };
}

export function loadQueuedFavoritePodcast() {
  return async (dispatch, getState) => {
    const {
      favorites,
      user: { isLoggedIn },
    } = getState();

    if (!isLoggedIn) {
      return;
    }

    const queuedPodcast = favorites.queue.podcasts || [];

    await Promise.all(
      queuedPodcast.map(async (item) => {
        const isEpisode = item.id !== item.podcastId;

        try {
          const jwtToken = await fetchGigyaJWTPromise();

          await graphql.mutateFavorite(jwtToken, item.id, item.contentTypeId, true);

          dispatch({
            type: isEpisode ? favoritePodcastActions.FAVORITE_PODCAST_CLIP : favoritePodcastActions.FAVORITE_PODCAST,
            payload: item,
          });

          dispatch(
            showNotification({
              message: isEpisode
                ? NotificationTexts.PODCAST_CLIP_ADD_TO_COLLECTION
                : NotificationTexts.PODCAST_ADD_TO_COLLECTION,
            })
          );
        } catch (e) {
          // rollback
          dispatch({
            type: favoriteActions.ROLLBACK_FAVORITES,
            payload: favorites,
          });

          dispatch(
            showNotification({
              message: isEpisode
                ? NotificationTexts.PODCAST_CLIP_ADD_TO_COLLECTION_ERROR
                : NotificationTexts.PODCAST_ADD_TO_COLLECTION_ERROR,
              type: NotificationTypes.ERROR,
            })
          );
          logger.error(e);
        }
      })
    );
  };
}
